// FloatingOrbs.js
import React, { useEffect, useMemo, useRef } from "react";

/**
 * Responsive physics orbs (mobile-safe):
 * - Dynamically scales orb sizes to container bounds (prevents jitter on small screens)
 * - Reduces orb count on small canvases
 * - Auto-tunes physics strength based on available motion space
 * - Updates CSS --b-size on resize so visuals match physics radii
 * - Keeps transform-only painting (GPU friendly)
 */
export default function FloatingOrbs({ orbs = [], className = "" }) {
  const wrapRef = useRef(null);
  const rafRef = useRef(null);

  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  const stateRef = useRef({
    ready: false,
    w: 0,
    h: 0,
    items: [], // { id, r, x, y, vx, vy, mass, baseSize }
    scrollEnergy: 0,
    lastScrollY: 0,
    lastT: 0,
    activeIds: [],
    tightFactor: 1, // 0..1 (1 = lots of space, 0 = very tight)
  });

  // Helpers
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  const chooseActiveOrbs = (w, h, list) => {
    // Reduce orb count on small screens to avoid congestion + jitter
    const minDim = Math.min(w, h);

    // Very small / narrow
    if (w < 420 || minDim < 420) return list.slice(0, 3);
    // Small tablets / large phones
    if (w < 680 || minDim < 560) return list.slice(0, 4);
    // Medium
    if (w < 980) return list.slice(0, 5);

    return list; // desktop
  };

  const computeTightFactor = (w, h) => {
    // 1 when roomy, approaches 0 when space is tight
    const minDim = Math.min(w, h);
    // Tune these thresholds if needed
    const t = (minDim - 360) / (980 - 360);
    return clamp(t, 0.18, 1);
  };

  const computeRadius = (baseSize, w, h) => {
    const minDim = Math.min(w, h);

    // Global scale relative to typical design canvas
    const scale = clamp(minDim / 980, 0.45, 1);

    // Start with scaled size
    let size = baseSize * scale;

    // Hard caps so orbs can never dominate small screens
    const maxSizeByCanvas = minDim * 0.62; // visual cap
    size = Math.min(size, maxSizeByCanvas);

    // Minimum size so they remain visible
    size = Math.max(size, 140);

    // Also ensure physics can actually fit the circle with padding
    // (if the container is short, this prevents the “constant edge push” vibration)
    const safeMax = Math.max(120, minDim - 32); // leave at least 16px padding each side
    size = Math.min(size, safeMax);

    return size / 2;
  };

  const rebuildItems = (activeOrbs, w, h, prevItems = []) => {
    const prevMap = new Map(prevItems.map((p) => [p.id, p]));
    const items = [];

    const minDim = Math.min(w, h);
    const edgePadding = 10;

    for (let idx = 0; idx < activeOrbs.length; idx++) {
      const o = activeOrbs[idx];
      const r = computeRadius(o.size, w, h);

      // Ensure orb can move at least a little without constantly clamping
      const minX = r + edgePadding;
      const maxX = Math.max(minX + 1, w - r - edgePadding);
      const minY = r + edgePadding;
      const maxY = Math.max(minY + 1, h - r - edgePadding);

      // Try preserve previous position on resize (reduces “teleport feel”)
      const prev = prevMap.get(o.id);

      let x;
      let y;

      if (prev) {
        // Keep relative position with clamping
        x = clamp(prev.x, minX, maxX);
        y = clamp(prev.y, minY, maxY);
      } else {
        // Even distribution starter layout
        const pad = Math.max(18, r + 8);
        x = clamp(
          pad + ((w - pad * 2) * (idx + 1)) / (activeOrbs.length + 1),
          minX,
          maxX
        );
        y = clamp(pad + ((h - pad * 2) * ((idx % 3) + 1)) / 4, minY, maxY);
      }

      // Smaller initial velocity on small canvases
      const speedSeed = clamp(minDim / 980, 0.35, 1);
      const vx = (prev?.vx ?? (Math.random() * 2 - 1) * 0.55) * speedSeed;
      const vy = (prev?.vy ?? (Math.random() * 2 - 1) * 0.55) * speedSeed;

      // Mass proportional to area (stabilizes collisions)
      const mass = Math.max(1, (r * r) / 9000);

      items.push({ id: o.id, r, x, y, vx, vy, mass, baseSize: o.size });
    }

    return items;
  };

  // Build / rebuild particles on resize
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || reduceMotion) return;

    const st = stateRef.current;

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;

      st.w = cr.width;
      st.h = cr.height;

      if (st.w <= 10 || st.h <= 10) return;

      const activeOrbs = chooseActiveOrbs(st.w, st.h, orbs);
      st.activeIds = activeOrbs.map((o) => o.id);

      // Tight-space factor (drives physics auto-tuning)
      st.tightFactor = computeTightFactor(st.w, st.h);

      // Rebuild with position preservation
      st.items = rebuildItems(activeOrbs, st.w, st.h, st.items);

      if (!st.ready) {
        st.lastScrollY = window.scrollY || 0;
        st.lastT = performance.now();
        st.ready = true;
      }

      // Update CSS sizes immediately so visuals match physics radii
      // (only if nodes exist already)
      const spans = el.querySelectorAll("[data-orb-id]");
      spans.forEach((n) => {
        const id = n.getAttribute("data-orb-id");
        const p = st.items.find((it) => it.id === id);
        if (!p) return;
        n.style.setProperty("--b-size", `${p.r * 2}px`);
      });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [orbs, reduceMotion]);

  // Physics loop + scroll handling
  useEffect(() => {
    if (reduceMotion) return;

    const el = wrapRef.current;
    if (!el) return;

    const st = stateRef.current;

    // DOM node cache
    const nodes = new Map();
    const collectNodes = () => {
      const spans = el.querySelectorAll("[data-orb-id]");
      spans.forEach((n) => nodes.set(n.getAttribute("data-orb-id"), n));
    };
    collectNodes();

    // Base Tunables (desktop-ish)
    const base = {
      damping: 0.993,
      restitution: 0.92,
      idleWander: 0.022,
      maxSpeed: 2.25,
      edgePadding: 6,

      scrollImpulseStrength: 0.036,
      scrollEnergyDecay: 0.88,
      scrollPaintDecay: 0.92,

      globalDriftStrength: 0.012,
      globalDriftSpeed: 0.00012,

      collisionIterations: 2,
    };

    const onScroll = () => {
      const y = window.scrollY || 0;
      const dy = y - st.lastScrollY;
      st.lastScrollY = y;

      const abs = Math.abs(dy);
      if (abs < 1) return;

      const impulse = Math.min(42, abs) / 42;

      // On tight screens, clamp scroll energy harder to avoid jitter waves
      const cap = clamp(0.65 + st.tightFactor * 0.35, 0.65, 1);

      st.scrollEnergy = Math.min(
        cap,
        st.scrollEnergy * base.scrollEnergyDecay + impulse * 0.45
      );
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    const tick = (t) => {
      rafRef.current = requestAnimationFrame(tick);
      if (!st.ready) return;

      if (nodes.size < st.items.length) collectNodes();

      const dtMs = Math.min(34, t - st.lastT || 16);
      st.lastT = t;
      const dt = dtMs / 16.67;

      const w = st.w;
      const h = st.h;
      if (w <= 0 || h <= 0) return;

      const items = st.items;

      // Auto-tune based on tightness
      // (tightFactor ~ 0.18..1)
      const tf = st.tightFactor;

      const damping = 1 - (1 - base.damping) * tf; // slightly stronger damping on small screens
      const restitution = base.restitution * (0.78 + tf * 0.22); // softer bounces when tight
      const idleWander = base.idleWander * (0.35 + tf * 0.65); // less wander on small screens
      const maxSpeed = base.maxSpeed * (0.45 + tf * 0.55); // lower speed when tight
      const edgePadding = base.edgePadding;

      const scrollImpulseStrength =
        base.scrollImpulseStrength * (0.35 + tf * 0.65);
      const scrollPaintDecay = 0.9 + tf * 0.03; // decay a bit faster on small screens

      const globalDriftStrength = base.globalDriftStrength * (0.45 + tf * 0.55);
      const globalDriftSpeed = base.globalDriftSpeed;

      const collisionIterations = tf < 0.5 ? 1 : base.collisionIterations;

      // scroll energy decay
      const se = st.scrollEnergy;
      st.scrollEnergy *= scrollPaintDecay;

      // Integrate
      for (let i = 0; i < items.length; i++) {
        const p = items[i];

        // If orb is too large for the container, shrink its effective radius a bit
        // (prevents constant edge clamping loop)
        const minDim = Math.min(w, h);
        const hardCapR = Math.max(60, (minDim - 28) / 2);
        const r = Math.min(p.r, hardCapR);
        if (r !== p.r) {
          p.r = r;
          p.mass = Math.max(1, (r * r) / 9000);

          const node = nodes.get(p.id);
          if (node) node.style.setProperty("--b-size", `${r * 2}px`);
        }

        // idle wander
        const fx = (Math.random() * 2 - 1) * idleWander;
        const fy = (Math.random() * 2 - 1) * idleWander;

        // global drift field
        const gx =
          Math.sin(t * globalDriftSpeed + i * 0.7) * globalDriftStrength;
        const gy =
          Math.cos(t * globalDriftSpeed + i * 0.6) * globalDriftStrength;

        // scroll impulse (coherent jolt)
        const angle =
          (i / items.length) * Math.PI * 2 + (Math.random() - 0.5) * 0.28;
        const jx = Math.cos(angle) * se * scrollImpulseStrength;
        const jy = Math.sin(angle) * se * scrollImpulseStrength;

        p.vx += (fx + gx + jx) * dt;
        p.vy += (fy + gy + jy) * dt;

        // damping
        p.vx *= Math.pow(damping, dt);
        p.vy *= Math.pow(damping, dt);

        // clamp speed
        const sp = Math.hypot(p.vx, p.vy);
        if (sp > maxSpeed) {
          const s = maxSpeed / sp;
          p.vx *= s;
          p.vy *= s;
        }

        // integrate
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        // edges
        const minX = p.r + edgePadding;
        const maxX = w - p.r - edgePadding;
        const minY = p.r + edgePadding;
        const maxY = h - p.r - edgePadding;

        // If the container is *extremely* tight, min can exceed max.
        // Handle gracefully by pinning near center instead of vibrating.
        if (minX >= maxX || minY >= maxY) {
          p.x = w * 0.5;
          p.y = h * 0.5;
          p.vx *= 0.5;
          p.vy *= 0.5;
        } else {
          if (p.x < minX) {
            p.x = minX;
            p.vx = Math.abs(p.vx) * restitution;
          } else if (p.x > maxX) {
            p.x = maxX;
            p.vx = -Math.abs(p.vx) * restitution;
          }

          if (p.y < minY) {
            p.y = minY;
            p.vy = Math.abs(p.vy) * restitution;
          } else if (p.y > maxY) {
            p.y = maxY;
            p.vy = -Math.abs(p.vy) * restitution;
          }
        }
      }

      // Orb-orb collisions
      for (let iter = 0; iter < collisionIterations; iter++) {
        for (let i = 0; i < items.length; i++) {
          for (let j = i + 1; j < items.length; j++) {
            const a = items[i];
            const b = items[j];

            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.hypot(dx, dy) || 0.0001;
            const minDist = a.r + b.r;

            if (dist < minDist) {
              const nx = dx / dist;
              const ny = dy / dist;

              const overlap = minDist - dist;

              const totalMass = a.mass + b.mass;
              const aPush = (b.mass / totalMass) * overlap;
              const bPush = (a.mass / totalMass) * overlap;

              a.x -= nx * aPush;
              a.y -= ny * aPush;
              b.x += nx * bPush;
              b.y += ny * bPush;

              const rvx = b.vx - a.vx;
              const rvy = b.vy - a.vy;
              const velAlongNormal = rvx * nx + rvy * ny;

              if (velAlongNormal < 0) {
                const e = restitution;
                const jImpulse =
                  (-(1 + e) * velAlongNormal) / (1 / a.mass + 1 / b.mass);

                const ix = jImpulse * nx;
                const iy = jImpulse * ny;

                a.vx -= (1 / a.mass) * ix;
                a.vy -= (1 / a.mass) * iy;
                b.vx += (1 / b.mass) * ix;
                b.vy += (1 / b.mass) * iy;
              }
            }
          }
        }
      }

      // Paint
      for (let i = 0; i < items.length; i++) {
        const p = items[i];
        const node = nodes.get(p.id);
        if (!node) continue;

        node.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate3d(-50%, -50%, 0)`;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [reduceMotion]);

  // Render only “active” orbs based on current container sizing.
  // We don’t know sizing on first render, so render all; resize observer
  // will shrink count by removing items from state and only those will animate.
  // Visual extras that aren’t animated won’t hurt, but we *do* want to avoid
  // extra DOM on mobile — so we derive active from state when ready.
  const renderedOrbs = (() => {
    const st = stateRef.current;
    if (!st.ready || !st.activeIds?.length) return orbs;
    const activeSet = new Set(st.activeIds);
    return orbs.filter((o) => activeSet.has(o.id));
  })();

  return (
    <div ref={wrapRef} className={className} aria-hidden="true">
      {renderedOrbs.map((o) => (
        <span
          key={o.id}
          data-orb-id={o.id}
          className="tpc-orb"
          style={{
            "--b-size": `${o.size}px`, // will be overridden by ResizeObserver with responsive value
            "--b-color": o.color,
            opacity: o.opacity,
            animationDelay: o.delay,
          }}
        />
      ))}
    </div>
  );
}
